// Global Audio Manager - handles one audio at a time across the entire app
// with fade in/out effects and iOS Safari compatibility

class GlobalAudioManager {
  constructor() {
    this.currentAudio = null;
    this.currentSource = null;
    this.fadeInterval = null;
    this.targetVolume = 0.08;
    this.isMuted = false;
  }

  // Create audio element with iOS Safari compatibility
  createAudio(url, loop = true, volume = 0.08) {
    const audio = new Audio();
    audio.preload = 'auto'; // Allow buffering
    audio.loop = loop;
    audio.volume = volume; // Start at target volume immediately
    audio.src = url;
    return audio;
  }

  // Fade in effect (1 second)
  fadeIn(audio, targetVolume = 0.08, duration = 1000) {
    return new Promise((resolve) => {
      if (this.fadeInterval) clearInterval(this.fadeInterval);
      
      const startVolume = 0;
      const volumeStep = targetVolume / (duration / 50);
      audio.volume = startVolume;
      
      this.fadeInterval = setInterval(() => {
        if (audio.volume < targetVolume - volumeStep) {
          audio.volume = Math.min(audio.volume + volumeStep, targetVolume);
        } else {
          audio.volume = targetVolume;
          clearInterval(this.fadeInterval);
          this.fadeInterval = null;
          resolve();
        }
      }, 50);
    });
  }

  // Fade out effect (1 second)
  fadeOut(audio, duration = 1000) {
    return new Promise((resolve) => {
      if (this.fadeInterval) clearInterval(this.fadeInterval);
      
      if (!audio || audio.paused) {
        resolve();
        return;
      }
      
      const startVolume = audio.volume;
      const volumeStep = startVolume / (duration / 50);
      
      this.fadeInterval = setInterval(() => {
        if (audio.volume > volumeStep) {
          audio.volume = Math.max(audio.volume - volumeStep, 0);
        } else {
          audio.volume = 0;
          audio.pause();
          clearInterval(this.fadeInterval);
          this.fadeInterval = null;
          resolve();
        }
      }, 50);
    });
  }

  // Play audio - stops any currently playing audio first
  async play(url, options = {}) {
    const { 
      loop = true, 
      volume = 0.08, 
      source = 'unknown',
      onError = null 
    } = options;

    // Stop current audio if playing
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    // Create new audio with target volume
    const targetVol = this.isMuted ? 0 : volume;
    const audio = this.createAudio(url, loop, targetVol);
    this.currentAudio = audio;
    this.currentSource = source;
    this.targetVolume = volume;

    // Try to play with proper error handling for iOS Safari
    try {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      return audio;
    } catch (error) {
      console.error('Audio play error:', error);
      if (onError) onError(error);
      return null;
    }
  }

  // Stop current audio immediately (no fade for responsive UX)
  async stop() {
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
      this.currentSource = null;
    }
  }

  // Pause current audio immediately (no fade for responsive UX)
  async pause() {
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
    }
  }

  // Resume current audio with fade in
  async resume() {
    if (this.currentAudio && this.currentAudio.paused) {
      try {
        await this.currentAudio.play();
        await this.fadeIn(this.currentAudio, this.isMuted ? 0 : this.targetVolume);
      } catch (error) {
        console.error('Audio resume error:', error);
      }
    }
  }

  // Set volume
  setVolume(volume) {
    this.targetVolume = volume;
    if (this.currentAudio && !this.isMuted) {
      this.currentAudio.volume = volume;
    }
  }

  // Toggle mute
  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.currentAudio) {
      this.currentAudio.volume = this.isMuted ? 0 : this.targetVolume;
    }
    return this.isMuted;
  }

  // Check if something is playing
  isPlaying() {
    return this.currentAudio && !this.currentAudio.paused;
  }

  // Get current source
  getCurrentSource() {
    return this.currentSource;
  }

  // Check if specific source is playing
  isSourcePlaying(source) {
    return this.isPlaying() && this.currentSource === source;
  }
}

// Singleton instance
const audioManager = new GlobalAudioManager();

export default audioManager;
